import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  DEVICE_ORDER_COUPON_QUOTE,
  DEVICE_ORDER_TASKS,
  ORDER_DEVICE_NOTES,
  ADD_ORDER_DEVICE_NOTE,
  CLOSE_DEVICE_ORDER_TASK,
  DEACTIVATE_ACCOUNT_FOR_DEVICE_ORDER,
  DISMISS_DEVICE_ORDER_TASK,
  RESTORE_DEVICE_ORDER_TASK,
  REPLACE_DEVICE_ORDER_COUPON,
  MINT_DEVICE_ORDER_COUPON,
  ORDER_DEVICE_CAN_BYPASS_PAYMENT_GATE,
  SYNC_COUPON_USAGE,
} from '@staff-app/graphql-client';
import type {
  DeviceOrderCouponQuote,
  DeviceOrderTask,
  DeviceOrderTaskPage,
  OrderDeviceNote,
  OrderDeviceTab,
} from '@staff-app/shared-types';

/** Debounce a fast-changing value (search box) — no shared helper in this repo. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Rows per page.
 *
 * This used to be the ONLY page: offset was pinned to 0, so the list simply stopped at the
 * newest 200 rows with nothing to say so. That was survivable while the table held a few
 * hundred ingested orders, but the backfill loaded history back to 2021, so most of the ledger
 * sat unreachable unless you knew to search for it. The caller now owns an offset.
 */
export const ORDER_PAGE_SIZE = 200;

export function useOrderDeviceTasks(
  tab: OrderDeviceTab,
  search: string,
  status?: string,
  couponUsed?: string,
  offset = 0,
) {
  const debouncedSearch = useDebounced(search.trim(), 300);
  return useQuery<{ deviceOrderTasks: DeviceOrderTaskPage }>(DEVICE_ORDER_TASKS, {
    variables: {
      filter: {
        tab,
        search: debouncedSearch || undefined,
        status: status || undefined,
        couponUsed: couponUsed || undefined,
      },
      pagination: { offset, limit: ORDER_PAGE_SIZE },
    },
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * The catalog-priced coupon value for one device line. Skipped until the dialog opens
 * (`enabled`) so the list view never triggers a Mongo price read per row.
 */
export function useDeviceOrderCouponQuote(
  invoiceId: string,
  productIndex: number | undefined,
  enabled: boolean,
  region?: string,
) {
  return useQuery<{ deviceOrderCouponQuote: DeviceOrderCouponQuote }>(DEVICE_ORDER_COUPON_QUOTE, {
    variables: { invoiceId, productIndex: productIndex ?? 0, region },
    skip: !enabled || !invoiceId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useOrderDeviceNotes(invoiceId: string | null) {
  return useQuery<{ orderDeviceNotes: OrderDeviceNote[] }>(ORDER_DEVICE_NOTES, {
    variables: { invoiceId: invoiceId ?? '' },
    skip: !invoiceId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useAddOrderDeviceNote() {
  return useMutation<{ addOrderDeviceNote: OrderDeviceNote }, { invoiceId: string; text: string }>(
    ADD_ORDER_DEVICE_NOTE,
    { refetchQueries: [ORDER_DEVICE_NOTES] },
  );
}

/**
 * May the signed-in staffer approve a coupon on an UNPAID invoice? Drives the UI only —
 * the server gates every write regardless, so a stale `false` can never open the gate and
 * a stale `true` still gets refused server-side.
 */
export function useOrderDeviceCanBypassPaymentGate(enabled = true) {
  const { data, loading } = useQuery<{ orderDeviceCanBypassPaymentGate: boolean }>(
    ORDER_DEVICE_CAN_BYPASS_PAYMENT_GATE,
    // Skipped for a PAID invoice — the overwhelmingly common case, where the gate is open
    // and the answer is irrelevant. cache-first, so the unpaid rows on a page share one
    // request rather than asking per row.
    { skip: !enabled, fetchPolicy: 'cache-first' },
  );
  return { canBypass: !!data?.orderDeviceCanBypassPaymentGate, loading };
}

export function useCloseDeviceOrderTask() {
  return useMutation<
    { closeDeviceOrderTask: DeviceOrderTask },
    {
      invoiceId: string;
      couponCode: string;
      productIndex?: number | null;
      /** Required by the server when the invoice is not PAID. */
      overrideReason?: string | null;
      /** ROOT ONLY: record a code Shopify reports as fully redeemed. */
      acceptRedeemed?: boolean | null;
    }
  >(CLOSE_DEVICE_ORDER_TASK, { refetchQueries: [DEVICE_ORDER_TASKS] });
}

export function useMintDeviceOrderCoupon() {
  return useMutation<
    { mintDeviceOrderCoupon: DeviceOrderTask },
    {
      invoiceId: string;
      productIndex?: number | null;
      amount?: number | null;
      /** 'us' | 'international' — pins the freight rate the server prices with. */
      region?: string | null;
      /** Required by the server when the invoice is not PAID. */
      overrideReason?: string | null;
    }
  >(MINT_DEVICE_ORDER_COUPON, { refetchQueries: [DEVICE_ORDER_TASKS] });
}

/**
 * Switch an account off for an unpaid device order. Refetches the list so the row immediately
 * shows "Account off" rather than offering the button again.
 *
 * The server does the real gating — approver allowlist, coupon must be redeemed, and a live Stripe
 * re-check — so a rejection here is expected and its message is worth showing verbatim.
 */
export function useDeactivateAccountForDeviceOrder() {
  return useMutation<
    { deactivateAccountForDeviceOrder: DeviceOrderTask },
    { invoiceId: string; reason: string }
  >(DEACTIVATE_ACCOUNT_FOR_DEVICE_ORDER, { refetchQueries: [DEVICE_ORDER_TASKS] });
}

/**
 * Dismiss a PAID order from the Active queue — its coupon was given by hand before the app.
 * Root-only on the server; refetches so the row leaves the queue at once.
 */
export function useDismissDeviceOrderTask() {
  return useMutation<
    { dismissDeviceOrderTask: DeviceOrderTask },
    { invoiceId: string; reason: string }
  >(DISMISS_DEVICE_ORDER_TASK, { refetchQueries: [DEVICE_ORDER_TASKS, ORDER_DEVICE_NOTES] });
}

/** Undo a dismissal — the order returns to the Active queue if it still qualifies. Root-only. */
export function useRestoreDeviceOrderTask() {
  return useMutation<{ restoreDeviceOrderTask: DeviceOrderTask }, { invoiceId: string }>(
    RESTORE_DEVICE_ORDER_TASK,
    { refetchQueries: [DEVICE_ORDER_TASKS, ORDER_DEVICE_NOTES] },
  );
}

/** On-demand "Code Used" sync; refetches the list so flipped rows show. */
export function useSyncCouponUsage() {
  return useMutation<{ syncCouponUsage: number }, { invoiceIds?: string[] }>(
    SYNC_COUPON_USAGE,
    { refetchQueries: [DEVICE_ORDER_TASKS] },
  );
}

/**
 * Replace an issued coupon code on one device line. Root-only on the server; refetches the notes
 * too, because the swap is recorded there.
 */
export function useReplaceDeviceOrderCoupon() {
  return useMutation<
    { replaceDeviceOrderCoupon: DeviceOrderTask },
    { invoiceId: string; productIndex: number; newCouponCode: string; reason: string }
  >(REPLACE_DEVICE_ORDER_COUPON, { refetchQueries: [DEVICE_ORDER_TASKS, ORDER_DEVICE_NOTES] });
}
